import { ObjectValues } from 'types';
import { FileType } from 'components/Dropzone/utils';
import { User } from 'models/User';
import { DataRoom } from 'router/subrouters/DataRoom/models/DataRoom';
import {
  DataRoomBuySideGroup,
  DataRoomMember,
} from 'router/subrouters/DataRoom/models/DataRoomMember';

export type DataRoomArchive = {
  _id: string;

  // Grouped state (progress for all instances)
  lastStep: DataRoomArchiveInstanceStep;
  stepsCompletedDates: {
    step: DataRoomArchiveInstanceStep;
    date: Date;
  }[];

  // Control step function execution
  executionArn: string;

  // Metadata
  createdAt: Date;
  updatedAt: Date;

  /* ---------------------------- Related entities ---------------------------- */

  // The actual archive instances
  instances: DataRoomArchiveInstance[];

  // Related data room (useful for authentication)
  dataRoomId: string;
  dataRoom: Partial<DataRoom> | null;

  // Data room member who created the archive
  dataRoomMemberId: string;
  dataRoomMember: Partial<DataRoomMember> | null;

  // User who created the archive
  userId: string;
  user: Partial<User> | null;
} & DataRoomArchiveSettings;

export type DataRoomArchiveInstance = {
  _id: string;

  type: DataRoomArchiveInstanceType;

  lastStep: DataRoomArchiveInstanceStep;
  stepsCompletedDates: {
    step: DataRoomArchiveInstanceStep;
    date: Date;
  }[];

  document?: FileType;

  // Metadata
  createdAt: Date;
  updatedAt: Date;

  /* ---------------------------- Related entities ---------------------------- */

  dataRoomBuySideGroupId?: string;
  dataRoomBuySideGroup?: Partial<DataRoomBuySideGroup> | null;

  dataRoomArchiveId: string;
  dataRoomArchive?: Partial<DataRoomArchive> | null;
} & DataRoomArchiveSettings;

export const DATA_ROOM_ARCHIVE_INSTANCE_STEP = {
  INITIATED: 'initiated',
  // Parallel steps
  GENERATING_QA_ACTIVITY_FILE: 'generating-qa-activity-file',
  GENERATING_INDEX_OF_FILES: 'generating-index-of-files',
  GENERATING_ACTIVITY_LOGS_FILE: 'generating-activity-logs-file',
  GENERATING_LIST_OF_MEMBERS_AND_ROLES_FILE:
    'generating-list-of-members-and-roles-file',
  GENERATING_DATA_ROOM_FILES_AND_FOLDERS:
    'generating-data-room-nodes-structure',
  // --------------
  ZIP_SAVE_CLEANUP_FILES: 'zip-save-cleanup-files',
  COMPLETED: 'completed',
} as const;

export type DataRoomArchiveInstanceStep = ObjectValues<
  typeof DATA_ROOM_ARCHIVE_INSTANCE_STEP
>;

export const DATA_ROOM_ARCHIVE_INSTANCE_TYPE = {
  SELL_SIDE: 'sell-side',
  BUY_SIDE: 'buy-side',
} as const;

export type DataRoomArchiveInstanceType = ObjectValues<
  typeof DATA_ROOM_ARCHIVE_INSTANCE_TYPE
>;

export const DATA_ROOM_ARCHIVE_FILE_TYPE = {
  PDF: 'pdf',
  EXCEL: 'excel',
};

export type DataRoomArchiveFileType = ObjectValues<
  typeof DATA_ROOM_ARCHIVE_FILE_TYPE
>;

export type DataRoomArchiveSettings = {
  turnOffProtections: boolean;
  hasFiles: boolean;
  hasActivityLogs: boolean;
  hasQA: boolean;
  hasMembers: boolean;
  fileType: DataRoomArchiveFileType;
  shareIndex: boolean;
  downloadIndex: boolean;

  //for old archive we don't have share index functionality
  isOldArchive: boolean;
};

export type CreateArchiveFormValues = {
  dataRoomBuySideGroupId?: string;
  turnOffProtections?: boolean;
} & DataRoomArchiveSettings;
